import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Users, Calendar, DollarSign, Shield, Zap, Heart, Star, ArrowRight, Check } from "lucide-react";
import dashboardImg from "@/assets/treasurer-dashboard-screenshot.png";
import membersImg from "@/assets/members-screenshot.png";
import eventsImg from "@/assets/events-screenshot.png";
const Index = () => {
  const features = [{
    title: "Member Management",
    description: "Efficiently manage PTA members, roles, and permissions with powerful tools",
    icon: Users
  }, {
    title: "Event Planning",
    description: "Organize and coordinate PTA events with ease using our intuitive calendar",
    icon: Calendar
  }, {
    title: "Fundraising",
    description: "Track donations and manage fundraising campaigns effortlessly",
    icon: DollarSign
  }, {
    title: "Secure & Compliant",
    description: "GDPR compliant with robust security measures to protect your data",
    icon: Shield
  }, {
    title: "Easy Integration",
    description: "Connect with Stripe, Zoom, and other essential services seamlessly",
    icon: Zap
  }, {
    title: "Community First",
    description: "Purpose-built specifically for school communities and PTAs",
    icon: Heart
  }];
  const testimonials = [{
    name: "Sarah Johnson",
    role: "PTA Chair, Hillside Primary",
    content: "PTA Hub has transformed how we manage our school community. What used to take hours now takes minutes. The member management and event planning features are game-changers.",
    rating: 5
  }, {
    name: "Michael Davies",
    role: "Treasurer, Oakwood Academy",
    content: "As treasurer, having all our financial tracking in one place has been incredible. The fundraising tools and expense management make my role so much easier.",
    rating: 5
  }, {
    name: "Emma Wilson",
    role: "Secretary, Riverside School",
    content: "The communication features have helped us engage with more parents than ever before. Our event attendance has increased by 40% since we started using PTA Hub.",
    rating: 5
  }];
  const benefits = ["Save 10+ hours per week on admin tasks", "Increase parent engagement by up to 50%", "Streamline fundraising campaigns", "Improve financial transparency", "Secure data management", "Multiple 3rd party integrations"];
  return <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-hero py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="container mx-auto text-center space-y-8 animate-fade-in relative z-10">
          <div className="inline-block">
            <Badge className="mb-4 px-4 py-2 text-sm">
              Trusted by over 500+ PTAs across the UK
            </Badge>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-foreground leading-tight">
            Transform Your PTA
            <span className="block bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Into a Powerhouse
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">The modern platform for school PTAs to manage members, events, fundraising, and more, all in one beautifully simple place.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated group text-lg px-8 py-6">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                View Pricing
              </Button>
            </Link>
          </div>
          <p className="text-sm text-muted-foreground pt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Question Section */}
      <section className="py-24 px-4 bg-card/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
                  Struggling to manage your PTA?
                </h2>
              </div>
              <div className="space-y-6">
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">Join loads of PTAs who have simplified their operations with PTA Hub's all-in-one platform.</p>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link to="/auth">
                    <Button size="lg" className="shadow-elevated text-lg px-8">
                      Get Started Free
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Link to="/features">
                    <Button size="lg" variant="outline" className="text-lg px-8">
                      See How It Works
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16 space-y-4">
            <Badge className="mb-2">Features</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Everything Your PTA Needs
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive tools designed specifically for school PTAs to streamline operations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <Card key={index} className="shadow-card hover:shadow-elevated transition-all duration-300 animate-fade-in border-2 hover:border-primary/20 group" style={{
              animationDelay: `${index * 100}ms`
            }}>
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-gradient-primary group-hover:scale-110 transition-transform">
                        <Icon className="h-7 w-7 text-primary-foreground" />
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                        <CardDescription className="text-base">{feature.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Screenshots Showcase */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto space-y-16">
          <div className="text-center space-y-4">
            <Badge className="mb-2">Platform Preview</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              See PTA Hub in Action
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Intuitive dashboards and tools that make PTA management a breeze
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <h3 className="text-3xl font-bold">Powerful Dashboards</h3>
              <p className="text-lg text-muted-foreground">
                Get a complete overview of your PTA's activities, finances, and upcoming events. Real-time insights help you make informed decisions quickly.
              </p>
              <ul className="space-y-3">
                {["Real-time analytics", "Financial overview", "Upcoming events", "Member activity tracking"].map((item, i) => <li key={i} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <Card className="overflow-hidden shadow-elevated">
                <img src={dashboardImg} alt="Dashboard Screenshot" className="w-full" />
              </Card>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Card className="overflow-hidden shadow-elevated">
                <img src={membersImg} alt="Members Management Screenshot" className="w-full" />
              </Card>
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-bold">Member Management Made Easy</h3>
              <p className="text-lg text-muted-foreground">
                Keep track of all your PTA members, their roles, and contact information in one organized place. Easily assign roles and manage permissions.
              </p>
              <ul className="space-y-3">
                {["Role-based permissions", "Contact management", "Activity tracking", "Custom Roles", "Optional Deputy roles"].map((item, i) => <li key={i} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <h3 className="text-3xl font-bold">Event Planning Simplified</h3>
              <p className="text-lg text-muted-foreground">
                Plan and manage all your PTA events with ease. From school fairs to fundraisers, keep everything organized and on schedule.
              </p>
              <ul className="space-y-3">
                {["Calendar integration", "RSVP tracking", "Volunteer management", "Automated reminders"].map((item, i) => <li key={i} className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <Card className="overflow-hidden shadow-elevated">
                <img src={eventsImg} alt="Events Management Screenshot" className="w-full" />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="mb-2">Why Choose PTA Hub</Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground">
                Built for School Communities
              </h2>
              <p className="text-xl text-muted-foreground">
                We understand the unique challenges PTAs face. That's why we've created a platform that addresses your specific needs.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                {benefits.map((benefit, i) => <div key={i} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>)}
              </div>
            </div>
            <div className="grid gap-6">
              <Card className="shadow-card p-6 border-2 border-primary/20">
                <div className="text-5xl font-bold text-primary mb-2">98%</div>
                <div className="text-muted-foreground">Customer satisfaction rate</div>
              </Card>
              <Card className="shadow-card p-6 border-2 border-primary/20">
                <div className="text-5xl font-bold text-primary mb-2">Email support</div>
                <div className="text-muted-foreground">Support availability</div>
              </Card>
              <Card className="shadow-card p-6 border-2 border-primary/20">
                <div className="text-5xl font-bold text-primary mb-2">100%</div>
                <div className="text-muted-foreground">GDPR compliant</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 bg-card/30">
        <div className="container mx-auto">
          <div className="text-center mb-16 space-y-4">
            <Badge className="mb-2">Testimonials</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Loved by PTA Leaders
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See what PTA chairs, treasurers, and secretaries are saying about PTA Hub
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => <Card key={index} className="shadow-card hover:shadow-elevated transition-all duration-300 animate-fade-in" style={{
            animationDelay: `${index * 100}ms`
          }}>
                <CardHeader>
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <CardDescription className="text-base leading-relaxed text-foreground">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t border-border pt-4">
                    <div className="font-semibold text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10" />
        <div className="container mx-auto text-center space-y-8 relative z-10">
          <Badge className="mb-4">Get Started Today</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground">
            Ready to Transform Your PTA?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">Join loads of PTAs who are already using PTA Hub to streamline their operations and achieve more</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link to="/auth">
              <Button size="lg" className="shadow-elevated group text-lg px-8 py-6">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                Contact Us
              </Button>
            </Link>
          </div>
          <p className="text-sm text-muted-foreground pt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>
    </div>;
};
export default Index;